import { Component, DestroyRef, Renderer2, ViewChild } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
  PoDisclaimer,
  PoModalComponent,
  PoNotificationService,
  PoRichTextComponent,
  PoTabComponent,
  PoTableColumn,
  PoTabsComponent,
} from "@po-ui/ng-components";
import { DependencyTrackComponentsComponent } from "../dependency-track-components/dependency-track-components.component";
import { finalize, map, merge, Observable, switchMap, tap } from "rxjs";
import { Vulnerability } from "../../interfaces/vulnerability";
import { Project } from "../../interfaces/project";
import { Severity } from "../../enums/severity";
import { DependencyTrackVulnerabilitiesService } from "../../services/dependency-track-vulnerabilities/dependency-track-vulnerabilities.service";
import { DependencyTrackVulnerabilityDetails } from "../../interfaces/dependency-track-vulnerability-details";
import { DependencyTrackVulnerability } from "../../interfaces/dependency-track-vulnerability";
import { DependencyTrackVulnerabilityMetrics } from "../../interfaces/dependency-track-vulnerability-metrics";
import { ComponentsResponse } from "../../interfaces/components-response";
import { Period } from "../../enums/period";
import { DependencyTrackProjectsService } from "../../services/dependency-track-projects/dependency-track-projects.service";

@Component({
  selector: "app-dependency-track-vulnerabilities",
  templateUrl: "./dependency-track-vulnerabilities.component.html",
  styleUrls: ["./dependency-track-vulnerabilities.component.css"],
})
export class DependencyTrackVulnerabilitiesComponent {
  @ViewChild(PoModalComponent, { static: true })
  private poModal: PoModalComponent;

  @ViewChild(PoTabsComponent, { static: true }) poTab: PoTabsComponent;

  @ViewChild(DependencyTrackComponentsComponent, { static: false })
  dependencyTrackComponents: DependencyTrackComponentsComponent;

  loading: boolean = true;
  downloadingCsv: boolean = false;
  vulnerabilitiesDetails: DependencyTrackVulnerabilityDetails;
  vulnerabilitiesColumns: Array<PoTableColumn> = [];
  disclaimerFilters: Array<PoDisclaimer> = [];
  project: Project;
  poRichTextToolbarActions: PoRichTextComponent;
  vulnerabilities: DependencyTrackVulnerability;
  vulnerabilitiesMetrics: DependencyTrackVulnerabilityMetrics;
  componentes: ComponentsResponse;

  constructor(
    private destroyRef: DestroyRef,
    private renderer: Renderer2,
    private poNotificationService: PoNotificationService,
    private dependencyTrackProjectsService: DependencyTrackProjectsService,
    private dependencyTrackVulnerabilitiesService: DependencyTrackVulnerabilitiesService
  ) {}

  open(project: Project): void {
    this.loading = true;
    this.project = project;
    this.poModal.title = `Vulnerabilidades - ${project.name}`;
    (this.poTab.tabs[0] as PoTabComponent).active = true;

    this.poModal.open();

    this.getVulnerabilities(project.id!)
      .pipe(
        map((vuln) => vuln?.id ?? ""),
        switchMap((vulnerabilitiesId) =>
          merge(
            this.getVulnerabilitiesDetails(project.id!, vulnerabilitiesId),
            this.getVulnerabilitiesMetrics(project.id!),
            this.getComponentes(project.id!)
          )
        ),
        finalize(() => (this.loading = false))
      )
      .subscribe();
  }

  close() {
    this.poModal.close();
  }

  generateCSV() {
    this.downloadingCsv = true;

    this.dependencyTrackProjectsService
      .getProjectsCsv(this.project.id || "")
      .subscribe({
        next: (blob) => {
          const link = this.renderer.createElement("a");
          const url = URL.createObjectURL(blob);

          this.renderer.setAttribute(link, "href", url);
          this.renderer.setAttribute(
            link,
            "download",
            `vulnerabilities_${this.project.id}.csv`
          );
          this.renderer.setStyle(link, "visibility", "hidden");
          this.renderer.appendChild(document.body, link);
          link.click();
          this.renderer.removeChild(document.body, link);
        },
        complete: () => (this.downloadingCsv = false),
        error: () => {
          this.downloadingCsv = false;

          this.poNotificationService.error(
            "Falha ao gerar o CSV. Tente novamente."
          );
        },
      });
  }

  private getVulnerabilitiesDetails(
    projectId: string,
    vulnerabilitiesId: string
  ): Observable<DependencyTrackVulnerabilityDetails> {
    return this.dependencyTrackVulnerabilitiesService
      .getLatestVulnerabilitiesDetails(projectId, vulnerabilitiesId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map((dependencyTrackVulnerabilityDetails) =>
          this.removeAliases(dependencyTrackVulnerabilityDetails)
        ),
        tap((dependencyTrackVulnerabilityDetails) =>
          this.setVulnerabilitiesDetails(dependencyTrackVulnerabilityDetails)
        )
      );
  }

  private getVulnerabilitiesMetrics(
    projectId: string
  ): Observable<DependencyTrackVulnerabilityMetrics> {
    return this.dependencyTrackVulnerabilitiesService
      .getVulnerabilitiesMetrics(projectId, Period.LastYear)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((dependencyTrackVulnerabilityMetrics) =>
          this.setVulnerabilitiesMetrics(dependencyTrackVulnerabilityMetrics)
        )
      );
  }

  private getVulnerabilities(
    projectId: string
  ): Observable<DependencyTrackVulnerability> {
    return this.dependencyTrackVulnerabilitiesService
      .getLatestVulnerabilities(projectId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((dependencyTrackVulnerabilities) =>
          this.setVulnerabilities(dependencyTrackVulnerabilities)
        )
      );
  }

  private getComponentes(
    projectId: string
  ): Observable<ComponentsResponse> {
    return this.dependencyTrackVulnerabilitiesService
      .getComponentes(projectId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((componentes) => 
          this.setComponentes(componentes)
        )
      );
  }

  private setVulnerabilitiesDetails(
    dependencyTrackVulnerabilityDetails: DependencyTrackVulnerabilityDetails
  ): void {
    this.vulnerabilitiesDetails = dependencyTrackVulnerabilityDetails;
  }

  private setVulnerabilitiesMetrics(
    dependencyTrackVulnerabilityMetrics: DependencyTrackVulnerabilityMetrics
  ): void {
    this.vulnerabilitiesMetrics = dependencyTrackVulnerabilityMetrics;
  }

  private setVulnerabilities(
    dependencyTrackVulnerabilities: DependencyTrackVulnerability
  ): void {
    this.vulnerabilities = dependencyTrackVulnerabilities;
  }

  private setComponentes(
    componentes: ComponentsResponse): 
    void {
    this.componentes = componentes;
  }

  private removeAliases(
    dependencyTrackVulnerabilityDetails: DependencyTrackVulnerabilityDetails
  ): DependencyTrackVulnerabilityDetails {
    type Finding =
      DependencyTrackVulnerabilityDetails["details"]["findings"][number];

    const sevRank: Record<string, number> = {
      CRITICAL: 0,
      HIGH: 1,
      MEDIUM: 2,
      LOW: 3,
      INFO: 4,
      UNASSIGNED: 5,
      UNKNOWN: 6,
    };

    const collectIds = (vuln: Finding["vulnerability"]): Set<string> => {
      const s = new Set<string>();
      if (vuln?.vulnId) s.add(String(vuln.vulnId));
      for (const a of vuln?.aliases ?? []) {
        if (a.cveId) s.add(String(a.cveId));
        if (a.ghsaId) s.add(String(a.ghsaId));
        if (a.vulnDbId) s.add(String(a.vulnDbId));
      }
      return s;
    };

    const intersects = <T>(a: Set<T>, b: Set<T>): boolean => {
      for (const v of a) if (b.has(v)) return true;
      return false;
    };

    const mergeGroup = (group: Finding[]): Finding => {
      const allIds = new Set<string>();
      let best: Finding | null = null;

      const better = (a: Finding | null, b: Finding): Finding => {
        if (!a) return b;
        const ra =
          sevRank[a.vulnerability.severity?.toUpperCase() || "UNKNOWN"] ?? 6;
        const rb =
          sevRank[b.vulnerability.severity?.toUpperCase() || "UNKNOWN"] ?? 6;
        if (ra !== rb) return ra < rb ? a : b;
        const ca = a.vulnerability.cvssV3BaseScore ?? -1;
        const cb = b.vulnerability.cvssV3BaseScore ?? -1;
        if (ca !== cb) return ca > cb ? a : b;
        const sa = a.vulnerability.source?.toUpperCase() || "";
        const sb = b.vulnerability.source?.toUpperCase() || "";
        if (sa === sb) return a;
        if (sb === "GITHUB") return b;
        if (sa === "GITHUB") return a;
        return a;
      };

      for (const f of group) {
        const ids = collectIds(f.vulnerability);

        ids.forEach((id) => allIds.add(id));
        best = better(best, f);
      }

      const aliasMap = new Map<
        string,
        { ghsaId?: string; cveId?: string; vulnDbId?: string }
      >();
      for (const f of group) {
        for (const a of f.vulnerability.aliases ?? []) {
          const key = `${a.cveId ?? ""}|${a.ghsaId ?? ""}|${a.vulnDbId ?? ""}`;

          if (!aliasMap.has(key)) aliasMap.set(key, a);
        }
      }

      const merged: Finding = {
        ...best!,
        vulnerability: {
          ...best!.vulnerability,
          cwes: Array.from(
            new Map(
              group
                .flatMap((g) => g.vulnerability.cwes ?? [])
                .map((c) => [c.cweId, c])
            ).values()
          ),
          cvssV3BaseScore:
            group.reduce(
              (m, f) => Math.max(m, f.vulnerability.cvssV3BaseScore ?? -1),
              best!.vulnerability.cvssV3BaseScore ?? -1
            ) ?? best!.vulnerability.cvssV3BaseScore,
          aliases: Array.from(aliasMap.values()),
        },
      };

      return merged;
    };

    const findings =
      dependencyTrackVulnerabilityDetails?.details?.findings ?? [];
    const byComponent: Map<string, Finding[]> = new Map();

    for (const f of findings) {
      const comp = f.component;
      const compKey =
        comp?.purl ?? `${comp?.name ?? "?"}@${comp?.version ?? "?"}`;
      const arr = byComponent.get(compKey) ?? [];

      arr.push(f);
      byComponent.set(compKey, arr);
    }

    const dedupedPerComponent: Finding[] = [];

    for (const [, compFindings] of byComponent) {
      const clusters: { ids: Set<string>; items: Finding[] }[] = [];

      for (const f of compFindings) {
        const ids = collectIds(f.vulnerability);
        let placed = false;

        for (const c of clusters) {
          if (intersects(c.ids, ids)) {
            f.vulnerability.aliases?.forEach((a) => {
              if (a.cveId) c.ids.add(String(a.cveId));
              if (a.ghsaId) c.ids.add(String(a.ghsaId));
              if (a.vulnDbId) c.ids.add(String(a.vulnDbId));
            });

            c.ids.add(String(f.vulnerability.vulnId));
            c.items.push(f);

            placed = true;

            break;
          }
        }

        if (!placed) {
          clusters.push({ ids: new Set([...ids]), items: [f] });
        }
      }

      for (const c of clusters) {
        dedupedPerComponent.push(mergeGroup(c.items));
      }
    }

    return {
      ...dependencyTrackVulnerabilityDetails,
      details: {
        ...dependencyTrackVulnerabilityDetails.details,
        findings: dedupedPerComponent,
      },
    };
  }
}
